import { createFinishStepContent } from '../FinishStepContent';
import { closeSidebarPanel, openPersonalizationSubmenu } from '../sidebarHelpers';

const editSubscriptionBanner = (baseUrl, navigate, currentDomain, user) => {
  return {
    id: 'editSubscriptionBanner',
    title: 'Edit the subscription banner',
    steps: [
      {
        selector: '[data-tour="webPersonalizationLink"]',
        content: <p>Click on the "Personalization" menu item in the navigation.</p>,
      },
      {
        actionBefore: async () => {
          navigate(`${baseUrl}/pages?type=bannerBlocks`, true);
          await openPersonalizationSubmenu('[data-tour="bannerBlocksLink"]');
        },
        selector: '[data-tour="bannerBlocksLink"]',
        content: <p>Click on the "Banner Blocks" in the navigation</p>,
      },
      {
        actionBefore: () => closeSidebarPanel(),
        selector: '[data-tour="bannerBlocksCards"]',
        content: (
          <p>
            To get you started, we have already created a couple of banner blocks for you to help you generate leads
            faster.
          </p>
        ),
      },
      {
        selector: '[data-tour="pagesSelect"]',
        content: (
          <>
            <p>Use this dropdown to select between different pages you can add Releva elements to.</p>
            <p>
              You can have multiple banner blocks on each page each with a different design and message, or addressing a
              different group of visitors.
            </p>
            <p>
              Each banner block is essentially an assignment of a given banner to a given page. This way, you can have
              the same banner show on multiple pages.
            </p>
          </>
        ),
      },
      {
        selector: '[data-tour="bannerBlocksName=Subscription form"]',
        content: <p>This is the subscription banner block.</p>,
      },
      {
        selector: '[data-tour="bannerBlocksName=Subscription form"] [data-tour="bannerBlockNameLink"]',
        content: <p>Click on the banner block name to get started editing it.</p>,
      },
      {
        actionBefore: () =>
          document
            .querySelector('[data-tour="bannerBlocksName=Subscription form"] [data-tour="bannerBlockNameLink"]')
            ?.click(),
        selector: '[data-tour="bannerBlockFormVisualContent"]',
        content: <p>Here, you can select which banner you wish to show on this page, as well as edit it on-the-spot</p>,
      },
      {
        selector: '[data-tour="bannerEditBtn"]',
        content: (
          <p>
            To change the banner content or the rules which determine which visitors see the banner, click "Edit" again.
          </p>
        ),
      },
      {
        actionBefore: () => document.querySelector('[data-tour="bannerEditBtn"]')?.click(),
        selector: '[data-tour="bannerFormVisualContent"]',
        stepInteraction: true,
        content: <p>Update the pictures and text of the banner using the visual editor and click next.</p>,
      },
      {
        selector: '[data-tour="bannerAudienceTab"]',
        content: <p>Click on the "Audience" tab.</p>,
      },
      {
        actionBefore: () => document.querySelector('[data-tour="bannerAudienceTab"]')?.click(),
        selector: '[data-tour="bannerSegmentSelect"]',
        content: (
          <>
            <p>The behavioral segment controls which set of visitors see the banner.</p>
            <p>
              Note that behavioral segments are evaluated continuously and are <strong>not</strong> real-time
            </p>
          </>
        ),
      },

      {
        selector: '[data-tour="bannerProfileFilterSelect"]',
        content: (
          <p>
            In order to exclude visitors who have already subscribed, we select the "Not subscribed" profile filter.
          </p>
        ),
      },
      {
        selector: '[data-tour="bannerTypeSelect"]',
        content: (
          <>
            <p>
              This setting allows you to select what happens after the user clicks on the banner, this includes closing
              it as well.
            </p>
            <p>If you choose the settings to Show Always it will pop up on every refresh on the page.</p>
            <p>
              The settings to "Show again" after cooldown allows you to choose how often the user will see that popup,
              you can show it every hour or even less often.
            </p>
          </>
        ),
      },
      {
        selector: '[data-tour="bannerShowWhenSelect"]',
        content: (
          <>
            <p>
              If you have a particular discount code, that goes with the subscription to your website, you can choose a
              time frame to show this subscription form.
            </p>
            <p>If there is no interval selected, the form will be on all the time.</p>
          </>
        ),
      },
      {
        selector: '[data-tour="bannerSaveBtn"]',
        content: <p>Click on the "Save" button.</p>,
      },
      {
        actionBefore: () => document.querySelector('[data-tour="bannerSaveBtn"]')?.click(),
        selector: '[data-tour="bannerBlockDisplayConfigurationTab"]',
        content: <p>Click on the "Display Configuration" tab.</p>,
      },
      {
        selector: '[data-tour="blockDisplayModeSelect"]',
        content: (
          <p>
            You can choose whether your banner is shown as a static image or a popup. In this case, we will work with
            the popup version.
          </p>
        ),
        actionBefore: () => document.querySelector('[data-tour="bannerBlockDisplayConfigurationTab"]')?.click(),
      },
      {
        selector: '[data-tour="triggerSelect"]',
        content: (
          <>
            <p>
              You can choose between a number of seconds or distance of scrolling before the popup is shown, or only
              show it when the user hovers at the upper section of the page, to navigate away from it.
            </p>
            <p>This will allow the user to check out the content on that page before the popup springs up.</p>
          </>
        ),
      },
      {
        selector: '[data-tour="urlFilterField"]',
        content: (
          <>
            <p>URL filter allows you to display the banner on a particular URL or hide it from some containing.</p>
            <p>
              You can do that based on the words that are included in the URL. For the subscription form that we're
              currently working on leave it on "Any URL".
            </p>
          </>
        ),
      },
      {
        selector: '[data-tour="bannerBlockSaveBtn"]',
        content: <p>Click on the "Save & Close" button.</p>,
      },
      {
        selector: '[data-tour="bannerBlocksName=Subscription form"]',
        content: (
          <>
            <p>
              You can check your subscription banner by typing <b>/?rlv_mode=debug</b> after your domain name. This will
              allow you to see all the elements created in Releva before you release them to your site visitors.
            </p>
            <a href={`${currentDomain.url}/?rlv_mode=debug`} target="_blank" rel="noopener noreferrer">
              Click here to check
            </a>
          </>
        ),
        actionBefore: () => document.querySelector('[data-tour="bannerBlockSaveBtn"]')?.click(),
      },
      {
        actionBefore: () => navigate(`${baseUrl}/guide`, true),
        selector: '[data-tour="userGuideHeading"]',
        content: createFinishStepContent(),
      },
    ],
    isComplete: !!user.userActions.find(
      (a) => a.action === 'taskCompleted' && a.context.task === 'editSubscriptionBanner'
    ),
  };
};

export default editSubscriptionBanner;
